import React, { useCallback, useEffect, useState } from 'react';
import { apiGet, apiPut } from '../../utils/api';
import './EmployeeMemoPanel.css';

// 직원 메모: 달이 아니라 사람에게 붙는다. 위에서 고치고, 아래에서 고친 이력을 본다.
// 대장 화면 오른쪽 패널로도, 따로 띄운 창(MemoWindow)으로도 쓴다.

const API = '/api/payroll';
// 대장 화면과 따로 띄운 메모 창이 주고받는 통로: {type: 'open', ...} 고른 직원 / {type: 'saved', ...} 저장 결과
export const MEMO_CHANNEL = 'payroll-memo';

const when = (stamp) => (stamp && stamp.at
  ? new Date(stamp.at).toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' })
  : '');
const who = (stamp) => (stamp ? stamp.by_name || stamp.by || '' : '');

function EmployeeMemoPanel({ companyId, companyName, target, onSaved, onClose, onPopout, standalone = false }) {
  const [memo, setMemo] = useState(null);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const { rowId, employeeId } = target;

  useEffect(() => {
    let alive = true;
    setError('');
    const show = (m) => { if (alive) { setMemo(m); setText(m.text || ''); } };
    // 직원 id 가 아직 없는 행은 메모도 없다(처음 저장할 때 id 가 붙는다)
    if (!employeeId) { show({ text: '', history: [] }); return () => { alive = false; }; }
    setMemo(null);
    apiGet(`${API}/companies/${companyId}/memos/${employeeId}`).then(show).catch((e) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [companyId, rowId, employeeId]);

  const dirty = memo != null && text.trim() !== (memo.text || '');

  const save = useCallback(async () => {
    if (!dirty || saving) return;
    setSaving(true);
    setError('');
    try {
      const saved = await apiPut(`${API}/companies/${companyId}/memo`, { row_id: rowId, text });
      setMemo(saved);
      setText(saved.text || '');
      if (onSaved) onSaved(saved);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, [companyId, rowId, text, dirty, saving, onSaved]);

  const history = (memo && memo.history) || [];

  return (
    <aside className={`memo-panel ${standalone ? 'standalone' : ''}`}>
      <header className="memo-head">
        <div className="memo-who">
          <b>{target.name || '(이름 없음)'}</b>
          {target.idNumber && <span>{String(target.idNumber).slice(0, 6)}</span>}
          {companyName && <span>{companyName}</span>}
        </div>
        {onPopout && <button type="button" className="memo-icon" onClick={onPopout} title="메모를 따로 창으로 띄웁니다. 다른 직원의 메모 단추를 누르면 그 창의 내용이 바뀝니다">↗ 새 창</button>}
        {onClose && <button type="button" className="memo-icon" onClick={onClose} title="닫기 (Esc)">✕</button>}
      </header>

      <section className="memo-edit">
        <textarea
          value={text}
          placeholder={memo == null ? '불러오는 중…' : '이 직원에 대한 메모를 적습니다. 어느 달 대장에서나 같은 메모가 보입니다.'}
          disabled={memo == null}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); save(); }
            if (e.key === 'Escape' && onClose && !dirty) onClose();
            e.stopPropagation();
          }}
        />
        <div className="memo-actions">
          <span className="memo-meta">
            {memo && memo.updated ? `마지막 수정 ${when(memo.updated)} · ${who(memo.updated)}` : '아직 메모가 없습니다'}
          </span>
          {dirty && <button type="button" className="memo-btn" onClick={() => setText(memo.text || '')}>되돌리기</button>}
          <button type="button" className="memo-btn primary" onClick={save} disabled={!dirty || saving} title="저장 (Ctrl+Enter)">
            {saving ? '저장 중…' : '저장'}
          </button>
        </div>
        {error && <div className="memo-error">⚠ {error}</div>}
      </section>

      <section className="memo-history">
        <h4>수정 이력 <span>{history.length}건</span></h4>
        {history.length === 0 && <p className="memo-empty">저장하면 여기에 이력이 쌓입니다.</p>}
        <ol>
          {history.map((h, i) => (
            <li key={`${h.at}-${i}`} className={i === 0 ? 'current' : ''}>
              <div className="memo-history-head">
                <span>{when(h)}</span>
                <span>{who(h)}</span>
                {i === 0 && <em>현재</em>}
              </div>
              {h.text ? <p>{h.text}</p> : <p className="memo-cleared">(메모를 비움)</p>}
            </li>
          ))}
        </ol>
      </section>
    </aside>
  );
}

export default EmployeeMemoPanel;
